import React from 'react';
import { CustomStepProps } from '../../types/survey';

export const OverallResults: React.FC<CustomStepProps> = ({
  onNext,
  onBack,
  surveyData,
}) => {
  const answers = surveyData?.answers || {};

  // Sleep score calculation (max 20)
  const calculateSleepScore = () => {
    let score = 0;
    const sleepHours = answers.sleep_hours;
    if (sleepHours === 1) score += 2;
    else if (sleepHours === 2) score += 3;
    else if (sleepHours === 3) score += 4;
    else if (sleepHours === 4) score += 3;
    else if (sleepHours === 5) score += 2;
    
    const sleepExtremes = answers.sleep_extremes;
    if (sleepExtremes === "Item 1") score += 0;
    else if (sleepExtremes === "Item 2") score += 1;
    
    const sleepQuality = answers.sleep_quality;
    if (sleepQuality) score += sleepQuality;
    
    const fatigue = answers.fatigue_frequency;
    if (fatigue) score += fatigue;
    
    const severeFatigue = answers.severe_fatigue;
    if (severeFatigue === 1) score += 5;
    else if (severeFatigue === 2) score += 4;
    else if (severeFatigue === 3) score += 3;
    else if (severeFatigue === 4) score += 2;
    else if (severeFatigue === 5) score += 1;
    
    return Math.round((score / 20) * 100);
  };

  // Body weight score (simple categorization)
  const calculateBodyWeightScore = () => {
    const bmi = answers.bmi || 0;
    
    // BMI scoring
    let bmiScore = 0;
    if (bmi >= 18.5 && bmi <= 24.9) bmiScore = 100;
    else if (bmi >= 25 && bmi <= 29.9) bmiScore = 70;
    else if (bmi < 18.5) bmiScore = 60;
    else bmiScore = 50;
    
    return bmiScore;
  };

  // Nutrition score (max 15)
  const calculateNutritionScore = () => {
    let score = 0;
    
    const fastFood = answers.fast_food_consumption;
    if (fastFood === 1) score += 5;
    else if (fastFood === 2) score += 4;
    else if (fastFood === 3) score += 3;
    else if (fastFood === 4) score += 2;
    else if (fastFood === 5) score += 1;
    
    const fruitsVeg = answers.fruits_and_vegetables;
    if (fruitsVeg) score += fruitsVeg;
    
    const nutritionRating = answers.nutrition_rating;
    if (nutritionRating) score += nutritionRating;
    
    return Math.round((score / 15) * 100);
  };

  // Physical activity score (max 25)
  const calculatePhysicalActivityScore = () => {
    let score = 0;
    
    const physicalActivity = answers.physical_activity;
    if (physicalActivity) score += physicalActivity;
    
    const activityFrequency = answers.physical_activity_frequency;
    if (activityFrequency) score += activityFrequency;
    
    const moderateActivity = answers.moderate_activity;
    if (moderateActivity === 1) score += 1;
    else if (moderateActivity === 2) score += 2;
    else if (moderateActivity === 3) score += 3;
    else if (moderateActivity === 4) score += 4;
    else if (moderateActivity === 5) score += 5;
    
    const intenseActivity = answers.intense_activity;
    if (intenseActivity === 1) score += 1;
    else if (intenseActivity === 2) score += 2;
    else if (intenseActivity === 3) score += 3;
    else if (intenseActivity === 4) score += 4;
    else if (intenseActivity === 5) score += 5;
    
    const activityLevel = answers.physical_activity_level;
    if (activityLevel) score += activityLevel;
    
    return Math.round((score / 25) * 100);
  };

  // Mental wellness score (max 40)
  const calculateMentalWellnessScore = () => {
    let score = 0;
    
    // Page 9 questions
    const purpose = answers.purpose;
    if (purpose === 1) score += 5;
    else if (purpose === 2) score += 4;
    else if (purpose === 3) score += 3;
    else if (purpose === 4) score += 2;
    else if (purpose === 5) score += 1;
    
    const careForSelf = answers.care_for_self;
    if (careForSelf === 1) score += 5;
    else if (careForSelf === 2) score += 4;
    else if (careForSelf === 3) score += 3;
    else if (careForSelf === 4) score += 2;
    else if (careForSelf === 5) score += 1;
    
    const improvement = answers.importance_of_improvement;
    if (improvement) score += improvement;
    
    const support = answers.support;
    if (support) score += support;
    
    // Page 10 questions
    const problemDealing = answers.problem_dealing;
    if (problemDealing) score += problemDealing;
    
    const explosiveReactions = answers.explosive_reactions;
    if (explosiveReactions === 1) score += 5;
    else if (explosiveReactions === 2) score += 4;
    else if (explosiveReactions === 3) score += 3;
    else if (explosiveReactions === 4) score += 2;
    else if (explosiveReactions === 5) score += 1;
    
    const boredom = answers.boredom;
    if (boredom === 1) score += 5;
    else if (boredom === 2) score += 4;
    else if (boredom === 3) score += 3;
    else if (boredom === 4) score += 2;
    else if (boredom === 5) score += 1;
    
    const breakdown = answers.breakdown;
    if (breakdown === 1) score += 5;
    else if (breakdown === 2) score += 4;
    else if (breakdown === 3) score += 3;
    else if (breakdown === 4) score += 2;
    else if (breakdown === 5) score += 1;
    
    return Math.round((score / 40) * 100);
  };

  // Addictions score (max 10)
  const calculateAddictionsScore = () => {
    let score = 0;
    
    const smoking = answers.smoking;
    if (smoking) score += smoking;
    
    const alcohol = answers.alcohol_consumption;
    if (alcohol) score += alcohol;
    
    return Math.round((score / 10) * 100);
  };

  // Calculate all scores
  const sleepScore = calculateSleepScore();
  const bodyWeightScore = calculateBodyWeightScore();
  const nutritionScore = calculateNutritionScore();
  const physicalActivityScore = calculatePhysicalActivityScore();
  const mentalWellnessScore = calculateMentalWellnessScore();
  const addictionsScore = calculateAddictionsScore();

  // Calculate overall score (average of all categories)
  const overallScore = Math.round(
    (sleepScore + bodyWeightScore + nutritionScore + physicalActivityScore + mentalWellnessScore + addictionsScore) / 6
  );

  // Get performance level
  const getPerformanceLevel = (score: number) => {
    if (score >= 80) return { text: 'Excellent', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (score >= 60) return { text: 'Good', color: 'text-blue-600', bgColor: 'bg-blue-100' };
    if (score >= 40) return { text: 'Fair', color: 'text-amber-600', bgColor: 'bg-amber-100' };
    return { text: 'Needs Improvement', color: 'text-red-600', bgColor: 'bg-red-100' };
  };

  const overallPerformance = getPerformanceLevel(overallScore);

  // Category data
  const categories = [
    { name: 'Sleep Quality', score: sleepScore, icon: '🌙' },
    { name: 'Body Weight', score: bodyWeightScore, icon: '⚖️' },
    { name: 'Nutrition', score: nutritionScore, icon: '🥗' },
    { name: 'Physical Activity', score: physicalActivityScore, icon: '💪' },
    { name: 'Mental Wellness', score: mentalWellnessScore, icon: '🧠' },
    { name: 'Addictions', score: addictionsScore, icon: '🚭' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue-lightest via-white to-royal-gold-lightest flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/30 rounded-full mb-4">
                <svg className="w-10 h-10 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold mb-4 text-gray-800">
                Your Overall Health Assessment
              </h1>
              <p className="text-xl text-gray-700">
                Comprehensive analysis of your lifestyle and wellness
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Overall Score Display */}
            <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-2xl p-8 mb-8">
              <div className="text-center">
                <div className="text-6xl font-bold mb-3 text-gray-800">{overallScore}%</div>
                <div className="text-xl text-gray-700 mb-2">Overall Health Score</div>
                <div className={`inline-flex items-center px-4 py-2 rounded-full ${overallPerformance.bgColor}`}>
                  <span className={`text-sm font-semibold ${overallPerformance.color}`}>
                    {overallPerformance.text}
                  </span>
                </div>
              </div>
            </div>

            {/* Category Scores Grid */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Performance by Category
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category, index) => {
                  const performance = getPerformanceLevel(category.score);
                  return (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{category.icon}</span>
                          <h3 className="font-semibold text-gray-800">{category.name}</h3>
                        </div>
                      </div>
                      <div className="relative pt-1">
                        <div className="flex mb-2 items-center justify-between">
                          <div>
                            <span className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${performance.bgColor} ${performance.color}`}>
                              {performance.text}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs font-semibold inline-block text-gray-700">
                              {category.score}%
                            </span>
                          </div>
                        </div>
                        <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                          <div 
                            style={{ width: `${category.score}%` }}
                            className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                              category.score >= 80 ? 'bg-green-500' :
                              category.score >= 60 ? 'bg-blue-500' :
                              category.score >= 40 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Key Insights */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Key Insights
              </h2>
              <div className="bg-gradient-to-r from-royal-gold-lightest to-royal-blue-lightest rounded-lg p-6">
                <div className="space-y-4">
                  {/* Strongest areas */}
                  {categories.filter(c => c.score >= 80).length > 0 && (
                    <div>
                      <h3 className="font-semibold text-green-800 mb-2">✅ Your Strongest Areas:</h3>
                      <ul className="space-y-1">
                        {categories.filter(c => c.score >= 80).map((cat, idx) => (
                          <li key={idx} className="text-gray-700 ml-4">
                            • {cat.name}: {cat.score}% - Excellent performance
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Areas for improvement */}
                  {categories.filter(c => c.score < 60).length > 0 && (
                    <div>
                      <h3 className="font-semibold text-amber-800 mb-2">⚠️ Areas for Improvement:</h3>
                      <ul className="space-y-1">
                        {categories.filter(c => c.score < 60).map((cat, idx) => (
                          <li key={idx} className="text-gray-700 ml-4">
                            • {cat.name}: {cat.score}% - Focus on improving this area
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Expert Recommendations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Our Experts Recommend
              </h2>
              <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed">
                  Effective motivation provides the energy essential not only to start an activity, but also to see it 
                  through to completion. People who feel strongly motivated carry out their plans and dreams. As Napoleon 
                  Bonaparte said, 'You have to wait three years from intention to execution.' If you want to be more 
                  effective than Napoleon or simply want to learn how to effectively create and maintain motivation – 
                  consult our experts.
                </p>
              </div>
            </div>

            {/* Action Plan */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Your Personalized Action Plan
              </h2>
              <div className="space-y-3">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Set realistic goals</span> - Start with small, achievable targets
                    </p>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-blue mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Track progress</span> - Monitor improvements weekly
                    </p>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Stay consistent</span> - Build healthy habits gradually
                    </p>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-blue mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Get support</span> - Work with our health experts
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Health Journey Timeline */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Health Journey</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-4 text-center">
                  <div className="text-2xl mb-2">📊</div>
                  <span className="text-sm font-semibold text-gray-900">Week 1-2</span>
                  <p className="text-xs text-gray-600 mt-1">Assessment & Planning</p>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4 text-center">
                  <div className="text-2xl mb-2">🎯</div>
                  <span className="text-sm font-semibold text-gray-900">Week 3-4</span>
                  <p className="text-xs text-gray-600 mt-1">Implementation</p>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4 text-center">
                  <div className="text-2xl mb-2">📈</div>
                  <span className="text-sm font-semibold text-gray-900">Month 2-3</span>
                  <p className="text-xs text-gray-600 mt-1">Progress Tracking</p>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4 text-center">
                  <div className="text-2xl mb-2">🏆</div>
                  <span className="text-sm font-semibold text-gray-900">Month 4+</span>
                  <p className="text-xs text-gray-600 mt-1">Maintenance</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={onBack}
                className="px-8 py-3 bg-white border-2 border-royal-gold text-royal-gold font-semibold rounded-lg hover:bg-royal-gold hover:text-white transition-colors"
              >
                Previous
              </button>
              <button
                onClick={onNext}
                className="px-8 py-3 bg-royal-gold text-white font-semibold rounded-lg hover:bg-royal-gold-dark transition-colors"
              >
                Complete Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};